import { http, HttpResponse, type HttpHandler } from "msw";

import { API_PREFIX, type PaginationMeta } from "@nasam/kernel";
import type { AssetTypeDto } from "../data";
import { assetTypeCreateDtoSchema, assetTypeUpdateDtoSchema } from "../data";
import { archivedAssetTypeFixtures, assetTypeFixtures } from "./assetTypeFixtures";

const ARCHIVED_ASSET_TYPE_MATCH_MESSAGE =
  "يوجد بالفعل نوع أصل بنفس البيانات ضمن الأنواع المؤرشفة. هل ترغب في استعادته؟";

const ASSET_TYPES_PATH = `${API_PREFIX}/asset-types`;

export interface AssetTypesHandlerOptions {
  readonly apiBaseUrl: string;
}

function pageMeta(
  url: URL,
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (target: number) => {
    const next = new URL(url);
    next.searchParams.set("page", String(target));
    next.searchParams.set("limit", String(limit));
    return next.toString();
  };
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(1, totalPages)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}

function positive(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function includes(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
}

function dateMatches(
  value: string,
  from: string | null,
  to: string | null,
): boolean {
  const timestamp = Date.parse(value);
  return (
    (from === null || timestamp >= Date.parse(from)) &&
    (to === null || timestamp <= Date.parse(to))
  );
}

// Only the three columns the real API can sort by; sortBy itself defaults
// to createdAt, same as the documented default.
function sortValue(row: AssetTypeDto, sortBy: string | null): string {
  switch (sortBy ?? "createdAt") {
    case "nameAr":
      return row.nameAr;
    case "nameEn":
      return row.nameEn;
    default:
      return row.createdAt;
  }
}

function filtered(rows: readonly AssetTypeDto[], url: URL): AssetTypeDto[] {
  const params = url.searchParams;
  const search = params.get("search");
  const accountId = params.get("accountId");
  const sortBy = params.get("sortBy");
  // The API defaults to desc; only "asc" opts out of it.
  const direction = params.get("sort") === "asc" ? 1 : -1;
  return rows
    .filter(
      (row) =>
        search === null || includes(row.nameEn, search) || includes(row.nameAr, search),
    )
    .filter((row) => accountId === null || row.accountId === accountId)
    .toSorted((left, right) => {
      const a = sortValue(left, sortBy);
      const b = sortValue(right, sortBy);
      return a.localeCompare(b) * direction;
    });
}

export function createAssetTypesHandlers({
  apiBaseUrl,
}: AssetTypesHandlerOptions): readonly HttpHandler[] {
  const assetTypes = assetTypeFixtures.map((row) => ({
    ...row,
    fields: row.fields.map((field) => ({ ...field })),
  }));
  const archivedAssetTypes = archivedAssetTypeFixtures.map((row) => ({
    ...row,
    fields: row.fields.map((field) => ({ ...field })),
  }));

  return [
    http.get(`${apiBaseUrl}${ASSET_TYPES_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      const page = positive(url.searchParams.get("page"), 1);
      const limit = positive(url.searchParams.get("limit"), 10);
      const rows = filtered(assetTypes, url);
      return HttpResponse.json({
        data: rows.slice((page - 1) * limit, page * limit),
        meta: pageMeta(url, page, limit, rows.length),
      });
    }),
    http.get(`${apiBaseUrl}${ASSET_TYPES_PATH}/summary`, () =>
      HttpResponse.json({
        data: {
          totalAssetTypes: assetTypes.length,
          totalActiveAssetTypes: assetTypes.filter(
            (row) => row.status === "ACTIVE",
          ).length,
          totalInactiveAssetTypes: assetTypes.filter(
            (row) => row.status === "INACTIVE",
          ).length,
        },
      }),
    ),
    http.post(`${apiBaseUrl}${ASSET_TYPES_PATH}`, async ({ request }) => {
      const body = assetTypeCreateDtoSchema.parse(await request.json());

      // A create attempt whose name matches an archived type creates
      // nothing -- 409 with the trimmed record and a restore prompt instead
      // (used to be 201 with this same trimmed body -- see
      // createAssetTypesHttpRepository's own create()).
      const archivedMatch = archivedAssetTypes.find(
        (row) =>
          row.nameEn.toLocaleLowerCase() === body.nameEn.toLocaleLowerCase() ||
          row.nameAr.toLocaleLowerCase() === body.nameAr.toLocaleLowerCase(),
      );
      if (archivedMatch !== undefined) {
        return HttpResponse.json(
          {
            statusCode: 409,
            path: ASSET_TYPES_PATH,
            message: ARCHIVED_ASSET_TYPE_MATCH_MESSAGE,
            code: "ARCHIVED_ASSET_TYPE_FOUND",
            data: {
              id: archivedMatch.id,
              nameEn: archivedMatch.nameEn,
              nameAr: archivedMatch.nameAr,
            },
          },
          { status: 409 },
        );
      }

      // Real contract: a matching English name blocks first, then Arabic --
      // each with its own message, same as the documented 409.
      const enClash = assetTypes.some(
        (row) => row.nameEn.toLocaleLowerCase() === body.nameEn.toLocaleLowerCase(),
      );
      if (enClash) {
        return HttpResponse.json(
          { statusCode: 409, path: ASSET_TYPES_PATH, message: "English name already exists" },
          { status: 409 },
        );
      }
      const arClash = assetTypes.some(
        (row) => row.nameAr.toLocaleLowerCase() === body.nameAr.toLocaleLowerCase(),
      );
      if (arClash) {
        return HttpResponse.json(
          { statusCode: 409, path: ASSET_TYPES_PATH, message: "Arabic name already exists" },
          { status: 409 },
        );
      }

      const index = assetTypes.length + 1;
      const now = "2026-03-15T09:00:00.000Z";
      const id = `asset-type-${String(index).padStart(2, "0")}`;
      const createdById = "mock-user-01";
      // Doesn't attempt to resolve a real account identity -- MSW has no
      // caller context to infer a client's own account from, and this mock
      // only needs the created row to show up correctly in the list.
      const accountId = body.accountId ?? "own-account";
      const account = assetTypes.find((row) => row.accountId === accountId)?.account ?? {
        id: accountId,
        nameEn: "Own Account",
        nameAr: "الحساب الخاص",
      };

      const created: AssetTypeDto = {
        id,
        nameEn: body.nameEn,
        nameAr: body.nameAr,
        remarks: body.remarks ?? null,
        attachmentsAllowed: body.attachmentsAllowed,
        status: "ACTIVE",
        createdAt: now,
        updatedAt: now,
        accountId,
        account,
        createdById,
        updatedById: null,
        fields: body.fields.map((field, fieldIndex) => ({
          id: `${id}-field-${fieldIndex + 1}`,
          nameEn: field.nameEn,
          nameAr: field.nameAr,
          type: field.type,
          isRequired: field.isRequired,
          isUnique: field.isUnique,
          sortOrder: fieldIndex,
          createdAt: now,
          updatedAt: now,
          assetTypeId: id,
          createdById,
        })),
      };
      assetTypes.push(created);

      return HttpResponse.json(
        { message: "Created successfully", data: created },
        { status: 201 },
      );
    }),
    // Registered before the generic `/:id` GET -- `/:id` is one segment and
    // would otherwise capture the literal "archived" as an id.
    http.get(`${apiBaseUrl}${ASSET_TYPES_PATH}/archived`, ({ request }) => {
      const url = new URL(request.url);
      const page = positive(url.searchParams.get("page"), 1);
      const limit = positive(url.searchParams.get("limit"), 10);
      const search = url.searchParams.get("search");
      const accountId = url.searchParams.get("accountId");
      const status = url.searchParams.get("status");
      const sort = url.searchParams.get("sort") === "asc" ? 1 : -1;
      const rows = archivedAssetTypes
        .filter(
          (row) =>
            (search === null ||
              includes(row.nameEn, search) ||
              includes(row.nameAr, search) ||
              includes(row.account.nameEn, search) ||
              includes(row.account.nameAr, search)) &&
            (accountId === null || row.accountId === accountId) &&
            (status === null || row.status === status) &&
            dateMatches(
              row.archivedAt,
              url.searchParams.get("fromArchivedAt"),
              url.searchParams.get("toArchivedAt"),
            ),
        )
        .toSorted(
          (left, right) =>
            left.archivedAt.localeCompare(right.archivedAt) * sort,
        )
        .map((row) => ({
          id: row.id,
          nameEn: row.nameEn,
          nameAr: row.nameAr,
          status: row.status,
          account: row.account,
          fieldsCount: row.fieldsCount,
          archivedAt: row.archivedAt,
        }));
      return HttpResponse.json({
        data: rows.slice((page - 1) * limit, page * limit),
        meta: pageMeta(url, page, limit, rows.length),
      });
    }),
    http.get(`${apiBaseUrl}${ASSET_TYPES_PATH}/:id`, ({ params }) => {
      const found = assetTypes.find((row) => row.id === params.id);
      if (found === undefined) {
        return HttpResponse.json(
          {
            statusCode: 404,
            path: `${ASSET_TYPES_PATH}/${String(params.id)}`,
            message: "Asset type not found",
          },
          { status: 404 },
        );
      }
      return HttpResponse.json({ data: found });
    }),
    // Registered before the generic `/:id` PATCH -- `/:id` is one segment and
    // never matches `/:id/restore`, but keep the specific route first anyway.
    http.patch(`${apiBaseUrl}${ASSET_TYPES_PATH}/:id/restore`, ({ params }) => {
      const path = `${ASSET_TYPES_PATH}/${String(params.id)}/restore`;
      const index = archivedAssetTypes.findIndex((row) => row.id === params.id);
      if (index === -1) {
        return HttpResponse.json(
          { statusCode: 404, path, message: "Asset type not found" },
          { status: 404 },
        );
      }
      const [restored] = archivedAssetTypes.splice(index, 1);
      if (restored === undefined) throw new Error("unreachable");
      const active = {
        ...restored,
        status: "ACTIVE" as const,
        updatedAt: "2026-03-15T09:00:00.000Z",
      };
      assetTypes.push(active);
      return HttpResponse.json({
        data: active,
        message: "Asset type restored successfully.",
      });
    }),
    http.put(`${apiBaseUrl}${ASSET_TYPES_PATH}/:id`, async ({ request, params }) => {
      const path = `${ASSET_TYPES_PATH}/${String(params.id)}`;
      const found = assetTypes.find((row) => row.id === params.id);
      if (found === undefined) {
        return HttpResponse.json(
          { statusCode: 404, path, message: "Asset type not found" },
          { status: 404 },
        );
      }

      const body = assetTypeUpdateDtoSchema.parse(await request.json());

      if (body.nameEn !== undefined) {
        const clash = assetTypes.some(
          (row) =>
            row.id !== found.id &&
            row.nameEn.toLocaleLowerCase() === body.nameEn!.toLocaleLowerCase(),
        );
        if (clash) {
          return HttpResponse.json(
            { statusCode: 409, path, message: "English name already exists" },
            { status: 409 },
          );
        }
      }
      if (body.nameAr !== undefined) {
        const clash = assetTypes.some(
          (row) =>
            row.id !== found.id &&
            row.nameAr.toLocaleLowerCase() === body.nameAr!.toLocaleLowerCase(),
        );
        if (clash) {
          return HttpResponse.json(
            { statusCode: 409, path, message: "Arabic name already exists" },
            { status: 409 },
          );
        }
      }

      // `fields`, when sent, is the whole desired form -- matched against
      // what's already stored by label (nameEn + nameAr), not by id. While
      // relatedAssetsCount is 0 it freely replaces the existing set; once
      // any asset exists, dropping a stored field is refused (400).
      if (body.fields !== undefined) {
        const submitted = body.fields;
        const matchKey = (nameEn: string, nameAr: string) =>
          `${nameEn.toLocaleLowerCase()}::${nameAr.toLocaleLowerCase()}`;

        const seenEn = new Set<string>();
        const seenAr = new Set<string>();
        for (const field of submitted) {
          const en = field.nameEn.toLocaleLowerCase();
          const ar = field.nameAr.toLocaleLowerCase();
          if (seenEn.has(en) || seenAr.has(ar)) {
            return HttpResponse.json(
              {
                statusCode: 400,
                path,
                message: `Field name "${field.nameEn}" already exists`,
              },
              { status: 400 },
            );
          }
          seenEn.add(en);
          seenAr.add(ar);
        }

        const submittedKeys = new Set(
          submitted.map((field) => matchKey(field.nameEn, field.nameAr)),
        );
        const droppedStoredField = found.fields.some(
          (field) => !submittedKeys.has(matchKey(field.nameEn, field.nameAr)),
        );
        if (droppedStoredField && (found.relatedAssetsCount ?? 0) > 0) {
          return HttpResponse.json(
            {
              statusCode: 400,
              path,
              message:
                "Every stored field must be included while assets exist for this type",
            },
            { status: 400 },
          );
        }

        const now = "2026-03-15T09:00:00.000Z";
        const createdById = "mock-user-01";
        found.fields = submitted.map((field, index) => {
          const existing = found.fields.find(
            (row) =>
              matchKey(row.nameEn, row.nameAr) ===
              matchKey(field.nameEn, field.nameAr),
          );
          if (existing !== undefined) {
            return { ...existing, isRequired: field.isRequired, sortOrder: index };
          }
          return {
            id: `${found.id}-field-${String(Date.now())}-${String(index)}`,
            nameEn: field.nameEn,
            nameAr: field.nameAr,
            type: field.type,
            isRequired: field.isRequired,
            isUnique: field.isUnique,
            sortOrder: index,
            createdAt: now,
            updatedAt: now,
            assetTypeId: found.id,
            createdById,
          };
        });
      }

      const now = "2026-03-15T09:00:00.000Z";
      if (body.nameEn !== undefined) found.nameEn = body.nameEn;
      if (body.nameAr !== undefined) found.nameAr = body.nameAr;
      if (body.remarks !== undefined) {
        found.remarks = body.remarks.length > 0 ? body.remarks : null;
      }
      if (body.status !== undefined) found.status = body.status;
      found.updatedAt = now;

      return HttpResponse.json({ message: "Updated successfully", data: found });
    }),
    http.delete(`${apiBaseUrl}${ASSET_TYPES_PATH}/:id`, ({ params }) => {
      const index = assetTypes.findIndex((row) => row.id === params.id);
      if (index === -1) {
        return HttpResponse.json(
          {
            statusCode: 404,
            path: `${ASSET_TYPES_PATH}/${String(params.id)}`,
            message: "Asset type not found",
          },
          { status: 404 },
        );
      }
      assetTypes.splice(index, 1);
      return HttpResponse.json({ message: "Deleted successfully" });
    }),
  ];
}
